import type { EmailBlock, EmailBlockType, EmailDoc, TemplateSlot } from './types';

export const BLOCK_LABEL: Record<EmailBlockType, string> = {
  text: 'Paragraph',
  list: 'List',
  facts: 'Facts (label / value rows)',
  tiles: 'Number tiles',
  callout: 'Callout box',
  button: 'Button',
  note: 'Small note',
  slot: 'Built-in block',
};

/** Block types an operator can add (a built-in block only comes with the template). */
export const ADDABLE_BLOCKS: EmailBlockType[] = ['text', 'list', 'facts', 'tiles', 'callout', 'button', 'note'];

export function newBlock(type: EmailBlockType, slots: TemplateSlot[] = []): EmailBlock {
  switch (type) {
    case 'text':
      return { type, text: '' };
    case 'note':
      return { type, text: '' };
    case 'list':
      return { type, items: [''], style: 'bullets' };
    case 'facts':
      return { type, items: [{ label: '', value: '' }] };
    case 'tiles':
      return { type, items: [{ value: '', label: '' }] };
    case 'callout':
      return { type, tone: 'info', text: '' };
    case 'button':
      return { type, label: '', url: '' };
    case 'slot':
      return { type, name: slots[0]?.name ?? '' };
  }
}

export function moveBlock(doc: EmailDoc, from: number, to: number): EmailDoc {
  if (to < 0 || to >= doc.blocks.length || from === to) return doc;
  const blocks = doc.blocks.slice();
  const [moved] = blocks.splice(from, 1);
  blocks.splice(to, 0, moved);
  return { ...doc, blocks };
}

export const replaceBlock = (doc: EmailDoc, index: number, block: EmailBlock): EmailDoc => ({
  ...doc,
  blocks: doc.blocks.map((b, i) => (i === index ? block : b)),
});

export const removeBlock = (doc: EmailDoc, index: number): EmailDoc => ({
  ...doc,
  blocks: doc.blocks.filter((_, i) => i !== index),
});

export const insertBlock = (doc: EmailDoc, block: EmailBlock): EmailDoc => ({
  ...doc,
  blocks: [...doc.blocks, block],
});

/** Optional text fields are stored absent, not as empty strings. */
export function tidy(doc: EmailDoc): EmailDoc {
  const out: EmailDoc = { subject: doc.subject, blocks: doc.blocks };
  for (const field of ['eyebrow', 'kicker', 'headline', 'footer'] as const) {
    const value = doc[field]?.trim();
    if (value) out[field] = doc[field];
  }
  return out;
}

export const sameDoc = (a: EmailDoc, b: EmailDoc): boolean =>
  JSON.stringify(tidy(a)) === JSON.stringify(tidy(b));

/** Insert text into `value` where the selection is, replacing it. */
export function spliceAt(value: string, start: number, end: number, text: string): { value: string; caret: number } {
  return { value: value.slice(0, start) + text + value.slice(end), caret: start + text.length };
}
